import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import NavBar from '../components/layout/NavBar';
import SiteFooter from '../components/layout/SiteFooter';
import SEO from '../components/SEO';
import { useWaitlistModal } from '../context/WaitlistModalContext';
import { organizationSchema, websiteSchema } from '../schema';

const TEASERS = [
  {
    to: '/about',
    label: 'About Us',
    title: 'Why we built SaniQ',
    desc: 'The problem with what your shoes carry indoors, and the mission behind intelligent entryway disinfection.',
  },
  {
    to: '/features',
    label: 'Features',
    title: 'How SaniQ works',
    desc: 'Pressure-sensing, medical-grade disinfection, and the technology behind a 2-3 second clean.',
  },
  {
    to: '/blog',
    label: 'Blog',
    title: 'Hygiene science & product notes',
    desc: 'Research-backed reading on shoe-sole bacteria, entryway hygiene, and what smart doormats change.',
  },
];

const Home = () => {
  const { openWaitlistModal } = useWaitlistModal();

  return (
    <div className="relative w-full min-h-screen">
      <SEO
        title="World's First Smart Disinfectant Doormat"
        description="SaniQ is the world's first intelligent disinfectant door mat, removing 99.9% of germs, bacteria, and viruses from shoe soles in 2-3 seconds. Coming to Australia, 2027."
        path="/"
        schema={[organizationSchema(), websiteSchema()]}
      />
      <NavBar />

      {/* Hero: full-viewport looping video (background), untouched asset */}
      <section
        id="top"
        className="relative flex min-h-screen flex-col items-center overflow-hidden bg-[#050505] px-8 pb-28 pt-36 text-center max-md:justify-start max-md:pb-32 md:justify-center md:pb-24 md:pt-40"
      >
        <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
          <video
            className="absolute inset-0 z-0 h-full w-full origin-center scale-[0.94] object-cover brightness-110 md:scale-[1.04] md:brightness-[1.08]"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
          >
            <source src="/videos/saniq-preview.mp4" type="video/mp4" />
          </video>
        </div>
        <div
          className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-black/62 via-black/42 to-[#050505]"
          aria-hidden="true"
        />
        <motion.div
          className="relative z-10 flex w-full max-w-4xl flex-col max-md:min-h-[calc(100svh-7.5rem)] md:block"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="max-md:pt-4">
            <span className="mb-8 inline-block rounded-full border border-cyan-500/30 px-4 py-1 text-[10px] uppercase tracking-[0.3em] text-cyan-400">
              Coming 2027
            </span>
            <h1 className="mb-8 text-5xl font-extralight leading-none tracking-tighter text-white drop-shadow-sm md:text-7xl lg:text-8xl">
              Stop Germs <br />
              <span className="font-bold text-white">at the Door.</span>
            </h1>
            <p className="mx-auto max-w-2xl text-lg font-light leading-relaxed text-white/80 md:mb-10 md:text-xl">
              The first intelligent disinfectant door mat. Removes 99.9% of germs, bacteria, and viruses from shoe soles in 2-3 seconds.
            </p>
          </div>
          <div className="flex max-w-full flex-col items-center max-md:mt-auto max-md:translate-y-24 max-md:pb-8 md:mt-10 md:translate-y-24">
            <button
              type="button"
              onClick={openWaitlistModal}
              className="mb-6 rounded-full bg-cyan-500 px-10 py-4 text-sm font-bold uppercase tracking-widest text-black transition-transform hover:scale-[1.03] hover:bg-cyan-400 md:mb-10"
            >
              Join the revolution
            </button>
            <p className="mx-auto max-w-xl text-[11px] leading-relaxed text-white/40 md:text-xs">
              Background footage is illustrative only; design, behaviour, and final appearance may change before launch.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Condensed stat strip */}
      <section className="py-20 px-8 max-w-5xl mx-auto text-center">
        <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">The Problem</p>
        <h2 className="text-2xl md:text-4xl font-light mb-10 text-white leading-tight">
          Your Shoes Are Bringing Danger Inside
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="glass rounded-3xl p-8 text-center border border-white/10">
            <p className="text-4xl md:text-5xl font-extralight text-cyan-400 mb-2 tracking-tight">96%</p>
            <p className="text-white text-xs uppercase tracking-widest">Shoes Carry Bacteria</p>
          </div>
          <div className="glass rounded-3xl p-8 text-center border border-white/10">
            <p className="text-4xl md:text-5xl font-extralight text-cyan-400 mb-2 tracking-tight">99.9%</p>
            <p className="text-white text-xs uppercase tracking-widest">Germs Eliminated</p>
          </div>
          <div className="glass rounded-3xl p-8 text-center border border-white/10">
            <p className="text-4xl md:text-5xl font-extralight text-cyan-400 mb-2 tracking-tight">2–3s</p>
            <p className="text-white text-xs uppercase tracking-widest">Seconds to Clean</p>
          </div>
        </div>
        <p className="text-white/45 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
          SaniQ eliminates pathogens at the entry point with medical-grade disinfectant and automated spray technology, with no buttons, no effort, every time someone steps through the door.
        </p>
      </section>

      {/* Teasers to the rest of the site */}
      <section className="py-16 px-8 max-w-6xl mx-auto border-t border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TEASERS.map((teaser) => (
            <Link key={teaser.to} to={teaser.to} className="glass p-8 rounded-3xl block hover:border-cyan-500/30 border border-white/10 transition-colors group">
              <p className="text-cyan-400 font-mono text-[10px] tracking-[0.4em] uppercase mb-4">{teaser.label}</p>
              <h3 className="text-xl font-medium text-white mb-3 group-hover:text-cyan-400 transition-colors">{teaser.title}</h3>
              <p className="text-white/45 text-sm leading-relaxed mb-4">{teaser.desc}</p>
              <span className="text-cyan-400 text-xs font-bold uppercase tracking-widest">Read more →</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Condensed waitlist CTA */}
      <section className="py-16 px-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-cyan-500/5 blur-[150px] rounded-full translate-y-1/2" />
        <div className="max-w-3xl mx-auto glass rounded-3xl p-12 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-light mb-4 tracking-tight text-white">Launching 2027.</h2>
          <p className="text-white/45 text-sm md:text-base leading-relaxed max-w-xl mx-auto mb-8">
            Be first. Be protected. Join the waitlist for launch updates and founding-member benefits; see the full details on the {' '}
            <Link to="/waitlist" className="text-cyan-400 hover:text-cyan-300 transition-colors">Updates page</Link>.
          </p>
          <button
            type="button"
            onClick={openWaitlistModal}
            className="px-10 py-4 bg-cyan-500 text-black font-bold rounded-full text-sm uppercase tracking-widest hover:scale-105 transition-transform"
          >
            Register
          </button>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default Home;
